/**
 * ActionRow - Individual action row that opens the proposal slideout on click.
 */

import { useQueryClient } from '@tanstack/react-query';
import { ChevronRight } from '@untitledui/icons';
import type { FC, SVGProps } from 'react';
import React, { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';

import RecipientAvatar from '@/components/proposals/RecipientAvatar';
import { CheckboxBase } from '@/lib/base/forms/checkbox';
import { TruncatedNode, TruncatedText } from '@/lib/base/truncated-text';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import { logger } from '@/lib/logger';
import type { ProposalListItem } from '@/lib/proposals/core/types';
import { proposalDetailQuery } from '@/lib/proposals/query/queries';
import {
  getOnboardingConfig,
  type OnboardingConfig,
} from '@/lib/proposals/utils/onboardingConfig';

import { UNDO_TIMEOUT_MS } from './constants';

/**
 * The row's title line box. Everything on the main row — checkbox, icon,
 * and the trailing action links — centers against the title's 21px line so
 * the row reads as one line, without per-element pixel nudges.
 */
const TITLE_LINE_BOX = 'h-[21px] items-center';

/**
 * Color styles for onboarding icons (matches FeaturedIcon light-outline theme).
 */
const ONBOARDING_ICON_COLORS: Record<
  OnboardingConfig['color'],
  { container: string; icon: string }
> = {
  gray: {
    container: 'bg-primary border border-secondary',
    icon: 'text-tertiary',
  },
  brand: {
    container: 'bg-primary border border-utility-brand-200',
    icon: 'text-[var(--color-fg-brand-primary)]',
  },
  warning: {
    container: 'bg-primary border border-utility-warning-200',
    icon: 'text-utility-warning-700',
  },
  success: {
    container: 'bg-primary border border-utility-success-200',
    icon: 'text-utility-success-700',
  },
};

/**
 * Compact onboarding icon (24x24) matching RecipientAvatar xs size.
 */
function OnboardingIcon({
  icon: Icon,
  color,
}: {
  icon: FC<SVGProps<SVGSVGElement>>;
  color: OnboardingConfig['color'];
}) {
  const styles = ONBOARDING_ICON_COLORS[color];
  return (
    <div
      className={`size-6 rounded-full flex items-center justify-center ${styles.container}`}
    >
      <Icon className={`size-3.5 ${styles.icon}`} />
    </div>
  );
}

interface ActionRowProps {
  /** Action data */
  action: ProposalListItem;
  /** Organization ID for API calls */
  orgId: number;
  /** Whether this row is selected (checkbox checked) */
  isSelected: boolean;
  /** Toggle selection callback */
  onToggleSelection: (id: number, shiftKey?: boolean) => void;
  /** Callback when user starts an action (opens slideout) */
  onStartAction?: (proposalId: number) => void;
  /** Callback when user dismisses an action */
  onDismissAction: (proposalId: number) => Promise<void>;
  /** Optional ref to the first checkbox (for focus restoration from parent) */
  firstCheckboxRef?: React.RefObject<HTMLButtonElement | null>;
  /** Whether this is an onboarding action (hides dismiss, shows featured icon) */
  isOnboarding?: boolean;
}

/**
 * ActionRow - Individual action row component.
 * Clicking the row opens the proposal slideout. Checkbox and "Not now" have
 * their own click handlers with stopPropagation.
 */
export const ActionRow = React.memo<ActionRowProps>(
  ({
    action,
    orgId,
    isSelected,
    onToggleSelection,
    onStartAction,
    onDismissAction,
    firstCheckboxRef,
    isOnboarding = false,
  }) => {
    const queryClient = useQueryClient();
    const localCheckboxRef = useRef<HTMLButtonElement>(null);
    // Use forwarded ref from parent (for focus restoration) or fall back to local
    const checkboxRef = firstCheckboxRef ?? localCheckboxRef;
    const [isDismissing, setIsDismissing] = useState(false);

    // Prefetch on hover
    const handleMouseEnter = useCallback(() => {
      const cached = queryClient.getQueryData(
        proposalDetailQuery(orgId, action.id).queryKey
      );
      if (!cached) {
        queryClient
          .prefetchQuery(proposalDetailQuery(orgId, action.id))
          .catch(() => {});
      }
    }, [queryClient, orgId, action.id]);

    // Handle checkbox toggle for selection (supports shift-click range selection)
    const handleCheckboxClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation(); // Don't trigger row click
        onToggleSelection(action.id, e.shiftKey);
      },
      [onToggleSelection, action.id]
    );

    // Handle row click - opens action in the slideout panel
    const handleRowClick = useCallback(() => {
      if (onStartAction) {
        onStartAction(action.id);
      }
    }, [action.id, onStartAction]);

    // Set to track which dismiss operations were undone (by unique operation ID)
    const undoneOperationsRef = useRef<Set<string>>(new Set());

    // TODO: Dismissal-with-undo pattern duplicated with DashboardHome handleDismissSelected.
    // CANNOT extract full flow: single-operation uses timestamp+boolean tracking, bulk uses UUID+Map
    // for concurrent operations with partial rollback. State machines fundamentally differ
    // (single-operation vs multi-operation). SAFE extraction boundary: only toast construction could be shared. See issue #3389.
    const handleNotNow = useCallback(async () => {
      setIsDismissing(true);

      // Clear selection immediately so bulk actions cannot run on this hidden item
      // during the 5-second undo window (fixes P2 issue where hidden items remain selectable)
      if (isSelected) {
        onToggleSelection(action.id);
      }
      // Generate unique ID for this dismiss operation to isolate undo tracking
      const operationId = `${action.id}-${Date.now()}`;

      try {
        // Show undo toast immediately
        const toastId = toast('Action dismissed. Undo?', {
          duration: UNDO_TIMEOUT_MS,
          action: {
            label: 'Undo',
            onClick: () => {
              undoneOperationsRef.current.add(operationId);
              setIsDismissing(false);
              toast.dismiss(toastId);
            },
          },
        });

        // Wait for toast to expire or be dismissed
        await new Promise(resolve => setTimeout(resolve, UNDO_TIMEOUT_MS));

        // If THIS specific operation was undone, don't dismiss
        if (undoneOperationsRef.current.has(operationId)) {
          undoneOperationsRef.current.delete(operationId);
          return;
        }

        // Proceed with dismissal
        await onDismissAction(action.id);
      } catch (error) {
        // Rollback on error
        setIsDismissing(false);
        toast.error("We couldn't dismiss this action. Try again.");
        const normalizedError =
          error instanceof Error ? error : new Error(String(error));
        logger.error('Failed to dismiss action', normalizedError, {
          component: 'ActionRow',
          actionId: action.id,
          orgId,
          originalErrorType: error?.constructor?.name || typeof error,
          originalError: String(error),
        });
      }
    }, [onDismissAction, action, orgId, isSelected, onToggleSelection]);

    // Handle "Not now" with stopPropagation so row click doesn't fire
    const handleNotNowClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        handleNotNow();
      },
      [handleNotNow]
    );

    // Extract action metadata
    const title = action.title || 'Untitled action';
    const description = action.description || '';
    const actionLabel = 'View';

    // Get onboarding config for icon/color (only when isOnboarding is true)
    const onboardingConfig = isOnboarding ? getOnboardingConfig(action) : null;

    if (isDismissing) {
      return (
        <div role='status' aria-live='polite' className='sr-only'>
          {title} dismissed
        </div>
      );
    }

    return (
      <div
        className='bg-primary rounded-[4px] border border-secondary overflow-hidden hover:border-primary transition-colors cursor-pointer'
        onMouseEnter={handleMouseEnter}
        onClick={handleRowClick}
        role='button'
        tabIndex={0}
        aria-label={`Open ${title}`}
      >
        <div className='px-4 py-3'>
          {/* Main row: checkbox, avatar, title+desc, action links - all on one line */}
          <div className='flex items-start gap-3 min-h-9'>
            {/* Interactive checkbox for selection (supports shift-click) - hidden on mobile */}
            <button
              ref={checkboxRef}
              type='button'
              onClick={handleCheckboxClick}
              className={`hidden sm:flex flex-shrink-0 ${TITLE_LINE_BOX} focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-focus-ring rounded`}
              aria-label={
                isSelected
                  ? `Deselect ${title}`
                  : `Select ${title}. Shift-click to select a range.`
              }
              aria-checked={isSelected}
              role='checkbox'
            >
              <CheckboxBase isSelected={isSelected} />
            </button>

            {/* Icon: centered on the title's line box (it is taller than the
                line, so it overhangs evenly above and below the title text) */}
            <div className={`flex flex-shrink-0 ${TITLE_LINE_BOX}`}>
              {onboardingConfig ? (
                <OnboardingIcon
                  icon={onboardingConfig.icon}
                  color={onboardingConfig.color}
                />
              ) : (
                <RecipientAvatar action={action} size='xs' />
              )}
            </div>

            {/* Title and description */}
            <div className='flex-1 min-w-0'>
              {/* Title + description each clip to one line and reveal the
                  full text in a tooltip only when actually truncated. */}
              <TruncatedText
                text={title}
                className='text-[14px] font-semibold text-primary leading-[21px]'
              />
              {description && (
                <TruncatedNode className='text-[13px] text-secondary leading-[21px] [&_p]:inline'>
                  {/* Inline wrapper + inline paragraphs so the markdown sits
                      on the truncating line instead of forming its own block. */}
                  <MarkdownContent
                    content={description}
                    size='compact'
                    orgId={orgId}
                    as='span'
                  />
                </TruncatedNode>
              )}
            </div>

            {/* Mobile: chevron indicator */}
            <ChevronRight className='size-5 text-fg-quaternary flex-shrink-0 sm:hidden' />

            {/* Desktop: Action links - hidden on mobile */}
            <div
              className={`hidden sm:flex gap-3 flex-shrink-0 ml-auto ${TITLE_LINE_BOX}`}
            >
              {/* Hide "Not now" for onboarding actions - they cannot be dismissed from dashboard */}
              {!isOnboarding && (
                <button
                  type='button'
                  onClick={handleNotNowClick}
                  aria-label={`Not now – ${title}`}
                  className='text-[14px] font-semibold text-fg-quaternary hover:text-tertiary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-focus-ring'
                >
                  Not now
                </button>
              )}
              <span className='text-[14px] font-semibold text-[var(--color-fg-brand-primary)]'>
                {actionLabel}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

ActionRow.displayName = 'ActionRow';
